import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import type { ReactNode } from 'react'
import type { UserAvatar } from '@/graphql/schema'
import type { SupportedLanguage } from '@/i18n/language'
import { AppSheetContent } from '@/app/shell/app-sheet-content'
import { useCurrentUserProfile } from '@/entities/user/model/use-current-user-profile'
import { WorkspaceSheet } from '@/features/workspace/ui/workspace-sheet'
import { useNotificationCenter } from '@/shared/notifications/notification-center'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/shared/ui/sheet'
import {
  useAppSheetProfile,
  useInstantProfileUpdate,
} from '@/app/shell/use-profile-sheet-state'

type CurrentUserProfile = ReturnType<
  typeof useCurrentUserProfile
>['currentUser']

function getWorkspaceSheetWorkspace(currentUser: CurrentUserProfile) {
  if (!currentUser?.workspace) {
    return null
  }

  return {
    id: currentUser.workspace.id,
    name: currentUser.workspace.name,
  }
}

type LogoutPhase = 'idle' | 'confirming' | 'armed' | 'loggingOut'

function useLogoutConfirmation() {
  const [phase, setPhase] = useState<LogoutPhase>('idle')
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const navigate = useNavigate()

  const reset = useCallback(() => {
    setPhase('idle')
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    },
    [],
  )

  const handleLogout = useCallback(() => {
    if (phase === 'idle') {
      setPhase('confirming')
      timerRef.current = setTimeout(() => {
        setPhase('armed')
        timerRef.current = null
      }, 200)
    } else if (phase === 'armed') {
      setPhase('loggingOut')
      void navigate({ to: '/logout' })
    }
  }, [phase, navigate])

  return {
    logoutPhase: phase,
    handleLogout,
    resetLogout: reset,
  }
}

function ProfileSheetDialog({
  displayName,
  email,
  isSaving,
  logoutPhase,
  onAvatarChange,
  onLanguageChange,
  onLogout,
  onOpenChange,
  open,
  selectedAvatar,
  selectedLanguage,
  trigger,
}: {
  displayName: string
  email: string
  isSaving: boolean
  logoutPhase: LogoutPhase
  onAvatarChange: (avatar: UserAvatar) => void
  onLanguageChange: (language: SupportedLanguage) => void
  onLogout: () => void
  onOpenChange: (open: boolean) => void
  open: boolean
  selectedAvatar: UserAvatar | null
  selectedLanguage: SupportedLanguage
  trigger?: ReactNode
}) {
  const { t } = useTranslation()

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {trigger ? <SheetTrigger asChild>{trigger}</SheetTrigger> : null}
      <SheetContent>
        <SheetHeader>
          <SheetTitle>{t('profile:title')}</SheetTitle>
          <SheetDescription />
        </SheetHeader>
        <AppSheetContent
          displayName={displayName}
          email={email}
          isSaving={isSaving}
          logoutPhase={logoutPhase}
          onAvatarChange={onAvatarChange}
          onClose={() => onOpenChange(false)}
          onLanguageChange={onLanguageChange}
          onLogout={onLogout}
          selectedAvatar={selectedAvatar}
          selectedLanguage={selectedLanguage}
        />
      </SheetContent>
    </Sheet>
  )
}

function useAppSheetState(
  controlledOpen: boolean | undefined,
  controlledOnOpenChange: ((open: boolean) => void) | undefined,
) {
  const { currentUser } = useCurrentUserProfile()
  const { notify } = useNotificationCenter()
  const { i18n } = useTranslation()
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const [workspaceSheetMode, setWorkspaceSheetMode] = useState<
    'create' | 'edit' | null
  >(null)
  const open = controlledOpen ?? uncontrolledOpen
  const setOpen = controlledOnOpenChange ?? setUncontrolledOpen
  const { apiLanguage, currentAvatar } = useAppSheetProfile(currentUser)
  const changeI18nLanguage = useCallback(
    (language: SupportedLanguage) => {
      if (i18n.language !== language) {
        void i18n.changeLanguage(language)
      }
    },
    [i18n],
  )
  const profileUpdate = useInstantProfileUpdate({
    apiLanguage,
    changeI18nLanguage,
    currentAvatar,
    notify,
  })
  const { logoutPhase, handleLogout, resetLogout } = useLogoutConfirmation()
  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      resetLogout()
      setOpen(nextOpen)
    },
    [setOpen, resetLogout],
  )

  return {
    currentUser,
    handleLogout,
    handleOpenChange,
    logoutPhase,
    open,
    profileUpdate,
    setWorkspaceSheetMode,
    workspaceSheetMode,
  }
}

export function AppSheet({
  onOpenChange: controlledOnOpenChange,
  open: controlledOpen,
  trigger,
}: {
  onOpenChange?: (open: boolean) => void
  open?: boolean
  trigger?: ReactNode
}) {
  const {
    currentUser,
    handleLogout,
    handleOpenChange,
    logoutPhase,
    open,
    profileUpdate,
    setWorkspaceSheetMode,
    workspaceSheetMode,
  } = useAppSheetState(controlledOpen, controlledOnOpenChange)

  return (
    <>
      <ProfileSheetDialog
        displayName={currentUser?.name?.trim() || currentUser?.username || ''}
        email={currentUser?.email ?? ''}
        isSaving={profileUpdate.isSaving}
        logoutPhase={logoutPhase}
        onAvatarChange={(avatar) => void profileUpdate.changeAvatar(avatar)}
        onLanguageChange={(language) =>
          void profileUpdate.changeLanguage(language)
        }
        onLogout={handleLogout}
        onOpenChange={handleOpenChange}
        open={open}
        selectedAvatar={profileUpdate.selectedAvatar}
        selectedLanguage={profileUpdate.selectedLanguage}
        trigger={trigger}
      />
      <WorkspaceSheet
        key={workspaceSheetMode ?? 'closed'}
        mode={workspaceSheetMode ?? 'create'}
        open={workspaceSheetMode !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setWorkspaceSheetMode(null)
          }
        }}
        workspace={getWorkspaceSheetWorkspace(currentUser)}
      />
    </>
  )
}
